import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import FilterBar, { dateRangeForFilter, DEFAULT_DATE_PRESETS } from '../components/shared/FilterBar';
import { useResizableColumns, ResizeHandle, ColumnDivider, useSortableData, SortableHeader } from '../components/shared/tableUtils';
import Pagination from '../components/shared/Pagination';
// Charts migrated from chart.js to recharts. The recharts components live
// in components/dashboard/DashboardCharts.jsx and accept the same chart-state
// shape that the BE returns (counts/labels/buckets/totals/converted/etc).
import {
  CasesOverTimeChart,
  BookingsOverTimeChart,
  InquiryConversionChart,
  TopMediatorsChart,
} from '../components/dashboard/DashboardCharts';

/* ── Per-chart range dropdown (1M / 3M / 6M / YTD) ──────────────────────
 *
 * Same UX pattern as the Mediator Detail page's chart dropdowns. Each
 * Dashboard time-series chart owns its own range state; the dropdown
 * narrows the chart's visible bucket window without re-fetching from the
 * BE — we slice the array of buckets returned for the full 12-month
 * series. Default 6M (matches Mediator Detail's default).
 */
const DASH_RANGE_OPTIONS = [
  { key: 'all', label: 'All time' },
  { key: '1m',  label: '1M'  },
  { key: '3m',  label: '3M'  },
  { key: '6m',  label: '6M'  },
  { key: 'ytd', label: 'YTD' },
];

function ChartRangeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const current = DASH_RANGE_OPTIONS.find((o) => o.key === value) || DASH_RANGE_OPTIONS[0];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600,
          padding: '5px 10px', borderRadius: 6,
          border: '1px solid #e5e5e5', background: '#fff', color: '#0e1e20',
          cursor: 'pointer', lineHeight: 1,
        }}
      >
        {current.label}
        <svg width="9" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
          <path d="M1 1l4 4 4-4" stroke="#5b5a64" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(16,24,40,0.08)', zIndex: 50,
            minWidth: 84, padding: 4,
          }}
        >
          {DASH_RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              role="option"
              aria-selected={opt.key === value}
              type="button"
              onClick={() => { onChange(opt.key); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                fontFamily: 'Inter, sans-serif', fontSize: 12,
                padding: '6px 10px', borderRadius: 4, border: 'none',
                background: opt.key === value ? '#f3f4f6' : 'transparent',
                color: '#0e1e20', cursor: 'pointer', lineHeight: 1.4,
              }}
              onMouseEnter={(e) => { if (opt.key !== value) e.currentTarget.style.background = '#fafafa'; }}
              onMouseLeave={(e) => { if (opt.key !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Slice a time-series chart payload (Cases Over Time / Bookings Over
 * Time / Inquiry Conversion shape) to the selected range. Returns a
 * new object preserving all parallel arrays in lockstep.
 *
 *   1M  → bucket containing today only
 *   3M  → 3 buckets ending at today
 *   6M  → 6 buckets ending at today
 *   YTD → all buckets in the current calendar year
 *
 * Finds "today's bucket" by matching bucket.year + bucket.monthIdx
 * against now. Robust to either current-at-end (lastNMonths) layouts
 * AND the new current-at-position-8 layout for Cases Over Time.
 */
function sliceChartByRange(payload, range, now = new Date()) {
  if (!payload || !Array.isArray(payload.buckets) || payload.buckets.length === 0) {
    return payload;
  }
  const buckets = payload.buckets;
  // When the BE returned daily buckets (short date range like "This week"),
  // the data is already scoped to the exact range. The month-based slicer
  // below would incorrectly collapse all same-month days to one bucket.
  if (buckets[0]?.bucketType === 'day') {
    return payload;
  }
  const nowYear = now.getUTCFullYear();
  const nowMonth = now.getUTCMonth();
  // Find the "current" bucket's index. If not present (rare — e.g. BE
  // returned a window not containing today), fall back to the last one.
  let curIdx = buckets.findIndex((b) => b.year === nowYear && b.monthIdx === nowMonth);
  if (curIdx < 0) curIdx = buckets.length - 1;
  let startIdx;
  let endIdx;
  if (range === 'all') {
    startIdx = 0;
    endIdx = buckets.length - 1;
  } else if (range === 'ytd') {
    startIdx = buckets.findIndex((b) => b.year === nowYear);
    if (startIdx < 0) startIdx = curIdx;
    endIdx = curIdx;
  } else {
    const count = range === '1m' ? 1 : range === '3m' ? 3 : 6;
    endIdx = curIdx;
    startIdx = Math.max(0, endIdx - (count - 1));
  }
  const sliceArr = (k) => (Array.isArray(payload[k]) ? payload[k].slice(startIdx, endIdx + 1) : payload[k]);
  return {
    ...payload,
    labels: sliceArr('labels'),
    counts: sliceArr('counts'),
    percentages: sliceArr('percentages'),
    buckets: sliceArr('buckets'),
    totals: sliceArr('totals'),
    converted: sliceArr('converted'),
    notConverted: sliceArr('notConverted'),
    pending: sliceArr('pending'),
  };
}

/* ── Chart Configs ── */

// Cases Over Time — LINE chart with gradient fill
function buildCasesOverTimeData(series) {
  const gradientFill = (ctx) => {
    const chart = ctx.chart;
    const { ctx: context, chartArea } = chart;
    if (!chartArea) return 'rgba(17,94,89,0.1)';
    const gradient = context.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, 'rgba(17,94,89,0.10)');
    gradient.addColorStop(1, 'rgba(86,217,200,0)');
    return gradient;
  };
  return {
    labels: series?.labels ?? [''],
    datasets: [
      {
        label: 'Cases',
        // Plot raw monthly case counts. Earlier this used `percentages`
        // (each month normalized 0–100% against the peak), but the
        // y-axis now shows real numbers — feeding percentages would
        // make the line shape correct but the axis labels wrong.
        data: series?.counts ?? [0],
        borderColor: '#499e99',
        borderWidth: 2,
        backgroundColor: gradientFill,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: '#499e99',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };
}

// Helpers used by the Cases Over Time tooltip — translate the short
// month label ('Mar') into the full 'March 2026' string the design calls
// for, and compute the +/- delta vs the previous month bucket.
const SHORT_MONTH_TO_FULL = {
  Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April', May: 'May', Jun: 'June',
  Jul: 'July', Aug: 'August', Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
};

const SHORT_MONTH_TO_IDX = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

// Build "first day of month" / "last day of month" ISO date strings
// (YYYY-MM-DD) for a given month label + year. Used by chart click
// handlers to route to /cases?startDate=…&endDate=… etc.
function monthBoundaryRange(label, year) {
  const m = SHORT_MONTH_TO_IDX[label];
  if (m == null || !year) return { startDate: '', endDate: '' };
  const start = new Date(Date.UTC(year, m, 1));
  const endLastDay = new Date(Date.UTC(year, m + 1, 0));
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { startDate: fmt(start), endDate: fmt(endLastDay) };
}

// Resolve the dataIndex from a chart.js click event. Returns the index
// of the clicked element, or null when the user clicked empty space.
function getClickedIndex(event, chart) {
  if (!chart || !event) return null;
  const els = chart.getElementsAtEventForMode(event.native || event, 'nearest', { intersect: true }, true);
  return els && els.length ? els[0].index : null;
}

const casesOverTimeOptions = (chartsState, navigate) => ({
  responsive: true,
  maintainAspectRatio: false,
  // Click a point → drill into /cases for that month (Adam: "Make all
  // chart elements clickable, similar to what has been demonstrated in
  // the Lovable dashboard").
  onClick: (event, _activeEls, chart) => {
    if (!navigate) return;
    const idx = getClickedIndex(event, chart);
    if (idx == null) return;
    const buckets = chartsState?.casesOverTime?.buckets;
    const bucket = buckets?.[idx];
    if (!bucket) return;
    const { startDate, endDate } = monthBoundaryRange(bucket.label, bucket.year);
    if (startDate && endDate) navigate(`/cases?startDate=${startDate}&endDate=${endDate}`);
  },
  // Without `intersect: false`, the cursor has to land exactly on the
  // (invisible — pointRadius:0) data point for the tooltip to trigger.
  // Index mode + intersect:false makes the entire vertical column at any
  // x-position hover-active, which is what users expect.
  interaction: { mode: 'index', intersect: false, axis: 'x' },
  hover: { mode: 'index', intersect: false, axis: 'x' },
  // Reserve a little padding at the top + bottom so the curve doesn't
  // clip against the chart edges when a month hits 100% (peak) or 0%.
  layout: { padding: { top: 12, bottom: 8 } },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      mode: 'index',
      intersect: false,
      axis: 'x',
      backgroundColor: '#ffffff',
      borderColor: '#e5e5e5',
      borderWidth: 1,
      titleColor: '#0e1e20',
      bodyColor: '#0e1e20',
      footerColor: '#4087fc',
      titleFont: { family: 'Inter Tight', size: 12, weight: '600' },
      bodyFont: { family: 'Inter Tight', size: 11 },
      footerFont: { family: 'Inter Tight', size: 11, weight: '500' },
      padding: 12,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        // Title: 'November 2025'
        title: (items) => {
          const it = items[0];
          if (!it) return '';
          const idx = it.dataIndex;
          const buckets = chartsState?.casesOverTime?.buckets;
          if (buckets && buckets[idx]?.year && buckets[idx]?.label) {
            return `${SHORT_MONTH_TO_FULL[buckets[idx].label] || buckets[idx].label} ${buckets[idx].year}`;
          }
          const label = it.label || '';
          const now = new Date();
          return `${SHORT_MONTH_TO_FULL[label] || label} ${now.getFullYear()}`;
        },
        // Body: 'Total Cases: 10'
        label: (item) => {
          const idx = item.dataIndex;
          const count = chartsState?.casesOverTime?.counts?.[idx];
          return count != null ? `Total Cases: ${count}` : '';
        },
        // Footer: '-1 vs Oct' — month-over-month delta vs previous bucket.
        footer: (items) => {
          const idx = items[0]?.dataIndex;
          const counts = chartsState?.casesOverTime?.counts || [];
          const labels = chartsState?.casesOverTime?.labels || [];
          if (idx == null || idx === 0) return '';
          const cur = counts[idx] ?? 0;
          const prev = counts[idx - 1] ?? 0;
          const diff = cur - prev;
          const sign = diff > 0 ? '+' : '';
          const prevLabel = labels[idx - 1] || '';
          return prevLabel ? `${sign}${diff} vs ${prevLabel}` : `${sign}${diff}`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { family: 'Inter Tight', size: 13 }, color: '#0e1e20' },
      border: { display: false },
    },
    y: {
      grid: { color: '#ffffff', lineWidth: 1 },
      beginAtZero: true,
      ticks: {
        font: { family: 'Inter Tight', size: 13 },
        color: '#0e1e20',
        precision: 0,
        callback: (val) => (Number.isInteger(val) ? val : ''),
      },
      border: { display: false },
    },
  },
});

// Bookings Over Time — line chart of cases with mediation_date set per
// month. Same shape as Cases Over Time, distinct color so the two
// charts read as separate series at a glance.
function buildBookingsOverTimeData(series) {
  const gradientFill = (ctx) => {
    const chart = ctx.chart;
    const { ctx: context, chartArea } = chart;
    if (!chartArea) return 'rgba(64,135,252,0.10)';
    const gradient = context.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, 'rgba(64,135,252,0.15)');
    gradient.addColorStop(1, 'rgba(64,135,252,0)');
    return gradient;
  };
  return {
    labels: series?.labels ?? [''],
    datasets: [
      {
        label: 'Bookings',
        data: series?.counts ?? [0],
        borderColor: '#4087fc',
        borderWidth: 2,
        backgroundColor: gradientFill,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: '#4087fc',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };
}

const bookingsOverTimeOptions = (chartsState, navigate) => ({
  responsive: true,
  maintainAspectRatio: false,
  onClick: (event, _activeEls, chart) => {
    if (!navigate) return;
    const idx = getClickedIndex(event, chart);
    if (idx == null) return;
    const buckets = chartsState?.bookingsOverTime?.buckets;
    const bucket = buckets?.[idx];
    if (!bucket) return;
    const { startDate, endDate } = monthBoundaryRange(bucket.label, bucket.year);
    if (startDate && endDate) navigate(`/cases?startDate=${startDate}&endDate=${endDate}`);
  },
  interaction: { mode: 'index', intersect: false, axis: 'x' },
  hover: { mode: 'index', intersect: false, axis: 'x' },
  layout: { padding: { top: 12, bottom: 8 } },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      mode: 'index',
      intersect: false,
      axis: 'x',
      backgroundColor: '#ffffff',
      borderColor: '#e5e5e5',
      borderWidth: 1,
      titleColor: '#0e1e20',
      bodyColor: '#0e1e20',
      titleFont: { family: 'Inter Tight', size: 12, weight: '600' },
      bodyFont: { family: 'Inter Tight', size: 11 },
      padding: 12,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        title: (items) => {
          const it = items[0];
          if (!it) return '';
          const idx = it.dataIndex;
          const buckets = chartsState?.bookingsOverTime?.buckets;
          if (buckets && buckets[idx]?.year && buckets[idx]?.label) {
            return `${SHORT_MONTH_TO_FULL[buckets[idx].label] || buckets[idx].label} ${buckets[idx].year}`;
          }
          const label = it.label || '';
          return `${SHORT_MONTH_TO_FULL[label] || label} ${new Date().getFullYear()}`;
        },
        label: (item) => {
          const idx = item.dataIndex;
          const count = chartsState?.bookingsOverTime?.counts?.[idx];
          return count != null ? `Total Bookings: ${count}` : '';
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { family: 'Inter Tight', size: 13 }, color: '#0e1e20' },
      border: { display: false },
    },
    y: {
      grid: { color: '#ffffff', lineWidth: 1 },
      beginAtZero: true,
      ticks: {
        font: { family: 'Inter Tight', size: 13 },
        color: '#0e1e20',
        precision: 0,
        callback: (val) => (Number.isInteger(val) ? val : ''),
      },
      border: { display: false },
    },
  },
});

// Inquiries Over Time — STACKED Converted / Not Converted bars with
// two y-axes (left: count of inquiries, right: 0–100% reference). Per
// user feedback: no line on the chart — the color split conveys the
// conversion rate.
// matching the Scale Portal layout. Two stacked datasets keyed on the
// raw counts derived from totals + converted%.
const CONVERSION_MONTHS_FALLBACK = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function buildConversionRateData(series) {
  const labels = series?.labels ?? CONVERSION_MONTHS_FALLBACK;
  const length = labels.length;
  const totals = series?.totals ?? Array(length).fill(0);
  const convertedPct = series?.converted ?? Array(length).fill(0);
  // Stacked Converted / Not Converted bars with 2 y-axes (left = count
  // of inquiries, right = 0–100% reference). No line — the color split
  // already conveys the conversion rate.
  const convertedCounts = totals.map((t, i) => Math.round((t * (convertedPct[i] || 0)) / 100));
  const notConvertedCounts = totals.map((t, i) => Math.max(0, t - convertedCounts[i]));
  return {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Not Converted',
        data: notConvertedCounts,
        backgroundColor: '#6eb2ae',
        barPercentage: 0.4,
        categoryPercentage: 0.8,
        yAxisID: 'y',
        stack: 'inquiries',
      },
      {
        type: 'bar',
        label: 'Converted',
        data: convertedCounts,
        backgroundColor: '#115e59',
        barPercentage: 0.4,
        categoryPercentage: 0.8,
        yAxisID: 'y',
        stack: 'inquiries',
      },
    ],
  };
}

const conversionRateOptions = (chartsState, navigate) => ({
  responsive: true,
  maintainAspectRatio: false,
  // Click a bar → drill into /inquiries for that month. Uses the
  // per-bucket year from the BE so a 12-month window that crosses a
  // year boundary navigates to the correct year.
  onClick: (event, _activeEls, chart) => {
    if (!navigate) return;
    const idx = getClickedIndex(event, chart);
    if (idx == null) return;
    const buckets = chartsState?.inquiryConversion?.buckets;
    const bucket = buckets?.[idx];
    if (!bucket) return;
    const { startDate, endDate } = monthBoundaryRange(bucket.label, bucket.year);
    if (startDate && endDate) navigate(`/inquiries?startDate=${startDate}&endDate=${endDate}`);
  },
  interaction: { mode: 'index', intersect: false, axis: 'x' },
  hover: { mode: 'index', intersect: false, axis: 'x' },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      mode: 'index',
      intersect: false,
      axis: 'x',
      backgroundColor: '#ffffff',
      borderColor: '#e5e5e5',
      borderWidth: 1,
      titleColor: '#0e1e20',
      bodyColor: '#0e1e20',
      titleFont: { family: 'Inter Tight', size: 12, weight: '500' },
      bodyFont: { family: 'Inter Tight', size: 11 },
      padding: 10,
      cornerRadius: 8,
      displayColors: true,
      boxWidth: 8,
      boxHeight: 8,
      boxPadding: 4,
      callbacks: {
        title: (items) => items[0]?.label || '',
        afterTitle: (items) => {
          const idx = items[0]?.dataIndex;
          const total = chartsState?.inquiryConversion?.totals?.[idx] ?? 0;
          return `Total inquiries: ${total}`;
        },
        label: (item) => {
          // Show the bar's count + the per-month percentage for that
          // half (Converted vs Not Converted).
          const idx = item.dataIndex;
          const conv = chartsState?.inquiryConversion?.converted?.[idx] ?? 0;
          const notConv = chartsState?.inquiryConversion?.notConverted?.[idx] ?? 0;
          const isConverted = item.dataset.label === 'Converted';
          return `${item.dataset.label}: ${isConverted ? conv : notConv}%`;
        },
      },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: { font: { family: 'Inter Tight', size: 13 }, color: '#0e1e20' },
      border: { display: false },
    },
    y: {
      position: 'left',
      stacked: true,
      beginAtZero: true,
      grid: { color: '#ffffff', lineWidth: 1 },
      ticks: {
        font: { family: 'Inter Tight', size: 13 },
        color: '#0e1e20',
        precision: 0,
        callback: (val) => (Number.isInteger(val) ? val : ''),
      },
      border: { display: false },
      title: {
        display: true,
        text: 'Inquiries',
        font: { family: 'Inter Tight', size: 10, weight: '500' },
        color: '#0e1e20',
      },
    },
    y1: {
      position: 'right',
      beginAtZero: true,
      min: 0,
      max: 100,
      grid: { display: false },
      ticks: {
        font: { family: 'Inter Tight', size: 13 },
        color: '#0e1e20',
        stepSize: 20,
        callback: (val) => val + '%',
      },
      border: { display: false },
      title: {
        display: true,
        text: 'Conversion rate',
        font: { family: 'Inter Tight', size: 10, weight: '500' },
        color: '#0e1e20',
      },
    },
  },
});

// Top Mediators — vertical bar chart with gradient color ramp.
// Colors are picked by rank: highest count gets the deepest teal,
// lower ranks fade toward neutral. Slice to actual N so a 3-mediator
// case manager doesn't end up with a 10-color bar.
const TOP_MEDIATOR_PALETTE = [
  '#115e59', '#55a29d', '#8ac9c5', '#d7edeb', '#ebf5f4',
  '#f4f5f6', '#f3f4f6', '#ebf5f4', '#d7edeb', '#8ac9c5',
];

function buildTopMediatorsData(top) {
  const names = top?.names ?? [];
  const percentages = top?.percentages ?? [];
  const counts = top?.counts ?? [];
  return {
    labels: names,
    datasets: [
      {
        label: 'Cases',
        data: percentages,
        rawCounts: counts,
        backgroundColor: names.map((_, i) => TOP_MEDIATOR_PALETTE[i % TOP_MEDIATOR_PALETTE.length]),
        borderRadius: 4,
        barPercentage: 0.7,
        categoryPercentage: 0.7,
      },
    ],
  };
}

const topMediatorsOptions = (chartsState, navigate) => ({
  responsive: true,
  maintainAspectRatio: false,
  // Click a bar → navigate to that mediator's detail page.
  onClick: (event, _activeEls, chart) => {
    if (!navigate) return;
    const idx = getClickedIndex(event, chart);
    if (idx == null) return;
    const ids = chartsState?.topMediators?.ids || [];
    const id = ids[idx];
    if (id) navigate(`/mediators/${id}`);
  },
  // Reserve headroom so the value badge above the tallest bar isn't clipped.
  layout: { padding: { top: 24 } },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { family: 'Inter Tight', size: 13 }, color: '#0e1e20' },
      border: { display: false },
    },
    y: {
      grid: { display: false },
      ticks: { display: false },
      border: { display: false },
      beginAtZero: true,
    },
  },
});

/* ── Sub-components ── */
// Keep this map in sync with Cases.jsx so the same case shows identical
// traffic-light colors on both pages.
const STATUS_COLOR_MAP = {
  green: '#22c55e',
  yellow: '#f59e0b',
  red: '#ef4444',
  grey: '#f59e0b',
};

function StatusDot({ color, label, statusText }) {
  const dotColor = STATUS_COLOR_MAP[color] || STATUS_COLOR_MAP.red;
  return (
    <span
      className="inline-flex items-center gap-1"
      title={statusText ? `${label}: ${statusText}` : label}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: dotColor }}
      />
      <span className="text-[11px] text-[#5b5a64] font-normal" style={{ fontFamily: 'Inter' }}>
        {label}
      </span>
    </span>
  );
}

function StatusCell({ status, briefLabel = 'Brief' }) {
  // Accept both BE shapes: { onboarding, payment, brief } from /dashboard
  // and { onboardingColor, paymentColor, briefColor } from /cases/:id. The
  // computePartyProgress algorithm is identical between endpoints so as long
  // as we read the right key, dashboard + case-detail always agree.
  const s = status || {};
  const onboardingColor = s.onboarding || s.onboardingColor || 'grey';
  const paymentColor = s.payment || s.paymentColor || 'grey';
  const briefColor = s.brief || s.briefColor || 'grey';
  return (
    <div className="flex items-center gap-3">
      <StatusDot color={onboardingColor} statusText={s.onboardingStatus} label="Onboarding" />
      <StatusDot color={paymentColor} statusText={s.paymentStatus} label="Payment" />
      <StatusDot color={briefColor} statusText={s.briefStatus} label={briefLabel} />
    </div>
  );
}

/* ── Tooltip Component ── */
function ChartTooltip({ title, body, badge, badgeType = 'positive', children }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div
          className="absolute z-50"
          style={{
            top: 28,
            left: -10,
            width: 240,
            filter: 'drop-shadow(0 4px 5.25px rgba(16,24,40,0.03)) drop-shadow(0 12px 14px rgba(16,24,40,0.08))',
          }}
        >
          <div
            className="bg-white rounded-lg border border-[#e5e5e5] flex flex-col gap-1.5"
            style={{ padding: '13px 12px' }}
          >
            <span className="text-xs font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
              {title}
            </span>
            <span
              className="text-[10px] font-normal text-[#0e1e20] whitespace-pre-line"
              style={{ fontFamily: 'Inter Tight', lineHeight: 1.6 }}
            >
              {body}
            </span>
            {badge && (
              <span
                className="inline-flex items-center gap-[5px] rounded-full self-start mt-1"
                style={{
                  backgroundColor: badgeType === 'positive' ? '#e8fdf5' : '#fde8e8',
                  padding: '4px 8px 5px 8px',
                }}
              >
                {badgeType === 'positive' ? (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M1.5 8.5L4.5 5.5L6.5 7.5L10.5 3.5" stroke="#00a666" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7.5 3.5H10.5V6.5" stroke="#00a666" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M1.5 3.5L4.5 6.5L6.5 4.5L10.5 8.5" stroke="#a60003" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7.5 8.5H10.5V5.5" stroke="#a60003" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                <span className="flex items-center gap-[3px]">
                  <span
                    className="text-[10px] font-medium"
                    style={{ fontFamily: 'Inter Tight', color: badgeType === 'positive' ? '#00a666' : '#a60003' }}
                  >
                    {badge.value}
                  </span>
                  <span className="text-[10px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>vs</span>
                  <span className="text-[10px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
                    {badge.label}
                  </span>
                </span>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Help Icon ── */
function HelpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="cursor-pointer">
      <circle cx="8" cy="8" r="6.5" stroke="#a4a3aa" strokeWidth="1" />
      <path d="M8 7.5V11" stroke="#a4a3aa" strokeWidth="1" strokeLinecap="round" />
      <circle cx="8" cy="5.5" r="0.5" fill="#a4a3aa" />
    </svg>
  );
}

/* ── KPI Card ── */
// Round a change-pill percentage; pass null/undefined (the "+N new" /
// isNew case) through untouched. NOT bounded — shows the real value.
const roundPct = (p) => (p == null ? p : Math.round(p));

function ChangeBadge({ change }) {
  const dir = change?.direction || 'flat';
  // Display the raw backend percent (rounded), uncapped — may exceed ±100.
  const pct = roundPct(change?.percent);
  const isNew = change?.isNew;
  const isDown = dir === 'down';
  const isFlat = dir === 'flat';
  const bg = isFlat ? '#f3f4f6' : (isDown ? '#fde8e9' : '#e8fdf5');
  const color = isFlat ? '#6b7280' : (isDown ? '#dc2626' : '#00a666');
  let label;
  if (isNew) {
    label = `+100%`;
  } else if (typeof pct === 'number') {
    const sign = pct > 0 ? '+' : '';
    label = `${sign}${pct}%`;
  } else {
    label = '\u2014';
  }
  return (
    <span className="inline-flex items-center gap-[5px] rounded-full" style={{ backgroundColor: bg, padding: '4px 8px 5px 8px' }}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        {isDown ? (
          <>
            <path d="M1.5 3.5L4.5 6.5L6.5 4.5L10.5 8.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.5 8.5H10.5V5.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </>
        ) : (
          <>
            <path d="M1.5 8.5L4.5 5.5L6.5 7.5L10.5 3.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.5 3.5H10.5V6.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </>
        )}
      </svg>
      <span className="flex items-center gap-[3px]">
        <span className="text-[10px] font-medium" style={{ fontFamily: 'Inter Tight', color }}>{label}</span>
        <span className="text-[10px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>vs</span>
        <span className="text-[10px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>{change?.compareLabel || 'last month'}</span>
      </span>
    </span>
  );
}

function KPICardDesign({ title, value, change, periodLabel }) {
  // Layout mirrors the Cases page KPICard: bordered header bar with the
  // title (and the period chip alongside) on top, big number + change
  // badge in the body below. Title strings drop the "This Period" suffix
  // because the period is already shown by the chip.
  return (
    <div className="flex flex-col bg-white rounded-[10px] border border-[#e5e5e5] overflow-hidden flex-1">
      <div className="flex items-center gap-2 px-3.5 py-3 border-b border-[#e5e5e5]">
        <span className="text-xs font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
          {title}
        </span>
        {periodLabel ? (
          <span
            className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-md whitespace-nowrap"
            style={{ padding: '3px 7px', fontFamily: 'Inter Tight' }}
          >
            {periodLabel}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col items-end justify-end gap-3.5 px-3.5 pt-5 pb-3.5">
        <span className="text-[30px] font-medium text-[#0e1e20] leading-none self-start" style={{ fontFamily: 'Inter Tight' }}>
          {value}
        </span>
        <ChangeBadge change={change} />
      </div>
    </div>
  );
}

/* ── Payment Distribution Legend Item ── */
function PaymentLegendItem({ color, label, amount }) {
  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-2">
        <span className="w-[13px] h-[13px] rounded-[3px]" style={{ backgroundColor: color }} />
        <span className="text-xs font-medium text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>{label}</span>
      </div>
      <span className="text-xs font-medium text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>{amount}</span>
    </div>
  );
}

/* ── Bar Value Label Plugin ── */
const barValuePlugin = {
  id: 'barValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        const label = dataset.rawCounts?.[index] ?? value;
        const x = bar.x;
        const y = bar.y - 12;
        ctx.save();
        ctx.font = '500 10px "Inter Tight"';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const textWidth = ctx.measureText(String(label)).width;
        const pw = 8, ph = 3;
        const bw = textWidth + pw * 2;
        const bh = 16;
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#e5e5e5';
        ctx.lineWidth = 1;
        const r = 6;
        const rx = x - bw / 2, ry = y - bh / 2;
        ctx.beginPath();
        ctx.moveTo(rx + r, ry);
        ctx.lineTo(rx + bw - r, ry);
        ctx.quadraticCurveTo(rx + bw, ry, rx + bw, ry + r);
        ctx.lineTo(rx + bw, ry + bh - r);
        ctx.quadraticCurveTo(rx + bw, ry + bh, rx + bw - r, ry + bh);
        ctx.lineTo(rx + r, ry + bh);
        ctx.quadraticCurveTo(rx, ry + bh, rx, ry + bh - r);
        ctx.lineTo(rx, ry + r);
        ctx.quadraticCurveTo(rx, ry, rx + r, ry);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#0e1e20';
        ctx.fillText(String(label), x, y);
        ctx.restore();
      });
    });
  },
};

/* ── Main Dashboard ── */
export default function Dashboard() {
  const navigate = useNavigate();
  const { user, caseManager } = useAuth();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [dashboardCases, setDashboardCases] = useState([]);
  const [loading, setLoading] = useState(true);
  // Whether the last /dashboard fetch failed. Previously a failure was
  // swallowed (.catch -> null -> setStats({})), so the page rendered
  // completely blank with no explanation and no way to retry.
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped by the retry button to re-run the fetch effect.
  const [refreshTick, setRefreshTick] = useState(0);
  const [mediatorsList, setMediatorsList] = useState([]);
  const [tableSearch, setTableSearch] = useState('');
  const [tableMediatorFilter, setTableMediatorFilter] = useState('');
  const [chartsSearch, setChartsSearch] = useState('');
  const [chartsMediatorFilter, setChartsMediatorFilterRaw] = useState('');
  const setChartsMediatorFilter = useCallback((v) => {
    setChartsMediatorFilterRaw(v);
    setTableMediatorFilter(v);
  }, []);
  const [chartsDateFilter, setChartsDateFilter] = useState('all');
  // Per-chart range dropdowns (1M / 3M / 6M / YTD). Each chart owns its
  // own range and slices the BE-returned series — no re-fetch needed.
  // Default 6M matches the Mediator Detail page convention.
  const [casesOverTimeRange, setCasesOverTimeRange] = useState('all');
  const [bookingsOverTimeRange, setBookingsOverTimeRange] = useState('all');
  const [inquiryConversionRange, setInquiryConversionRange] = useState('all');
  const slicedCasesOverTime = useMemo(
    () => sliceChartByRange(charts?.casesOverTime, casesOverTimeRange),
    [charts?.casesOverTime, casesOverTimeRange],
  );
  const slicedBookingsOverTime = useMemo(
    () => sliceChartByRange(charts?.bookingsOverTime, bookingsOverTimeRange),
    [charts?.bookingsOverTime, bookingsOverTimeRange],
  );
  const slicedInquiryConversion = useMemo(
    () => sliceChartByRange(charts?.inquiryConversion, inquiryConversionRange),
    [charts?.inquiryConversion, inquiryConversionRange],
  );
  const [chartsCustomStart, setChartsCustomStart] = useState('');
  const [chartsCustomEnd, setChartsCustomEnd] = useState('');
  // Column widths for the Upcoming Cases table — drag the right edge of
  // any header to resize. caseName is flex-1, so it grows to fill leftover
  // width and the table always spans 100% of its container.
  const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
    'dashboard.upcomingCases',
    { mediator: 220, caseName: 320, date: 140, plaintiff: 240 }
  );

  const firstName = user?.firstName || caseManager?.firstName || 'User';
  const initials = `${(user?.firstName || 'U')[0]}${(user?.lastName || '')[0] || ''}`.toUpperCase();

  const filteredDashboardCases = dashboardCases.filter((row) => {
    // Filter compares on userId (matches the canonical mediator list used
    // in BOTH the page-level filter and the Upcoming Cases dropdown).
    // Previously this compared on the mediator NAME string, which
    // diverged from the page-level filter and broke for unassigned cases.
    if (tableMediatorFilter && row.mediatorUserId !== tableMediatorFilter) return false;
    const q = tableSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(row.id).toLowerCase().includes(q) ||
      (row.mediator || '').toLowerCase().includes(q) ||
      (row.caseName || '').toLowerCase().includes(q)
    );
  });

  const { sortedRows: sortedDashboardCases, sort: tableSort, requestSort: requestTableSort } = useSortableData(
    filteredDashboardCases,
    {
      mediator: (r) => r.mediator,
      caseName: (r) => r.caseName,
      date: (r) => r.dateRaw,
    }
  );

  // Client-side pagination for the Upcoming Cases table. The /dashboard
  // endpoint already returns a bounded slice (top-N upcoming), so the
  // pagination here just chunks the in-memory list for display — no extra
  // network requests. Page resets to 1 whenever the visible row set
  // changes (sort/search/mediator filter).
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [upcomingPageSize, setUpcomingPageSize] = useState(10);
  useEffect(() => {
    setUpcomingPage(1);
  }, [tableSearch, tableMediatorFilter, upcomingPageSize, dashboardCases.length]);
  const upcomingTotal = sortedDashboardCases.length;
  const upcomingTotalPages = Math.max(1, Math.ceil(upcomingTotal / upcomingPageSize));
  const pageStart = (upcomingPage - 1) * upcomingPageSize;
  const pagedUpcomingCases = sortedDashboardCases.slice(pageStart, pageStart + upcomingPageSize);

  // The mediators list (for the FilterBar dropdown) is fetched once at
  // mount and intentionally NOT refreshed on filter change — the dropdown
  // shouldn't shrink based on what the other filters narrow to.
  useEffect(() => {
    const controller = new AbortController();
    api.get('/mediators?limit=200', { signal: controller.signal })
      .then(res => {
        const seen = new Set();
        const deduped = (res.data?.data || [])
          .filter(m => m.name?.trim())
          .filter(m => {
            const key = m.userId || `name:${m.name.trim().toLowerCase()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        setMediatorsList(deduped);
      })
      .catch(err => {
        if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') console.error(err);
      });
    return () => controller.abort();
  }, []);

  // Single source of truth for fetching dashboard data. Runs:
  //   1. on initial mount (immediate, no debounce — so KPIs + charts
  //      land together and there's no empty-chart flash)
  //   2. on every filter change (debounced 250ms so rapid typing/clicks
  //      don't fire one request per keystroke)
  // Both /dashboard (KPI stats) and /dashboard/charts (chart series)
  // are re-fetched on EVERY change so the entire dashboard reflects the
  // active filter — no field shows stale unfiltered data.
  const isFirstFetchRef = React.useRef(true);
  // Monotonically incrementing request id so a stale (slow) response
  // can't overwrite a fresher one. Each fetchAll() captures its id at
  // call time and only commits state if it's still the latest.
  const requestIdRef = React.useRef(0);
  useEffect(() => {
    const buildQs = () => {
      const params = new URLSearchParams();
      if (chartsMediatorFilter) params.set('mediatorId', chartsMediatorFilter);
      if (chartsSearch) params.set('search', chartsSearch);
      const { startDate, endDate } = dateRangeForFilter(
        chartsDateFilter,
        chartsCustomStart,
        chartsCustomEnd
      );
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      return params.toString();
    };
    const fetchAll = () => {
      const qs = buildQs();
      const myReqId = ++requestIdRef.current;
      const isLatest = () => myReqId === requestIdRef.current;
      // Show the spinner while the new data is in flight so the user
      // gets visual feedback that the filter is taking effect.
      setLoading(true);
      // CM-BE still owns: Inquiries KPI, Admin Fee KPI, Inquiry Conversion
      // chart (no scale-be equivalent / no admin-fee aggregate in mediator
      // analytics per scalemediation-be/src/controller/mediatorController.js).
      // Upcoming Cases table is now driven by scale-be (see scalePromise
      // below + buildUpcomingCasesFromScale call in the .then). Scale-BE's
      // /mediator/analytics/:user_id already returns the per-party
      // traffic-light colours (plaintiff/defendant .onboarding/.payment
      // /.brief) so the FE just flattens + filters — no colour math.
      const statsPromise = api
        .get(`/dashboard${qs ? `?${qs}` : ''}`)
        .then((res) => {
          if (!isLatest()) return null;
          return res.data?.data || res.data || {};
        })
        .catch((err) => {
          console.error('dashboard stats fetch failed', err);
          if (isLatest()) setLoadFailed(true);
          return null;
        });
      const chartsPromise = api
        .get(`/dashboard/charts${qs ? `?${qs}` : ''}`)
        .then((res) => res.data?.data || res.data || null)
        .catch((err) => {
          console.error('dashboard charts fetch failed', err);
          return null;
        });
      // All data comes from cm-be — no scale-be fan-out needed.
      // cm-be's /dashboard returns tiles + upcoming cases with traffic
      // lights; /dashboard/charts returns all chart series. Both honour
      // the FilterBar's date/mediator/search params.
      Promise.all([statsPromise, chartsPromise])
        .then(([cmStats, cmCharts]) => {
          if (!isLatest()) return;
          if (cmStats) setLoadFailed(false);
          setStats(cmStats || {});
          setCharts(cmCharts || {});
          setDashboardCases((cmStats?.upcomingCases || []).map((c) => ({
            ...c,
            mediator: c.mediatorName || c.mediator || '',
            caseName: c.caseName || c.case_name || '',
            dateRaw: c.date || c.mediation_date || '',
          })));
        })
        .finally(() => {
          if (isLatest()) setLoading(false);
        });
    };
    // Single code path: schedule the fetch on every run (immediate on
    // first mount, 250ms debounced on filter changes). The cleanup
    // cancels the queued timer so StrictMode's dev double-mount + rapid
    // filter changes can't fire duplicate network requests.
    const delay = isFirstFetchRef.current ? 0 : 250;
    isFirstFetchRef.current = false;
    const handler = setTimeout(fetchAll, delay);
    return () => clearTimeout(handler);
  }, [chartsMediatorFilter, chartsSearch, chartsDateFilter, chartsCustomStart, chartsCustomEnd, refreshTick]);

  return (
    <div className="flex flex-col justify-center bg-white">
      {/* ── Header Bar ── */}
      <div
        className="flex items-center justify-between bg-white border-b border-[#e5e5e5]"
        style={{ height: 60, padding: '14px 24px' }}
      >
        <span className="text-sm font-semibold text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>
          Dashboard
        </span>
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-[#115e59] flex items-center justify-center">
            <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Inter' }}>{initials}</span>
          </div>
        </div>
      </div>

      {/* ── Greeting ── */}
      <div style={{ padding: '24px 24px 12px 24px' }}>
        <h1
          className="text-[22px] font-medium text-[#0e1e20] leading-[1.25]"
          style={{ fontFamily: 'Inter Tight', letterSpacing: '-0.22px' }}
        >
          Welcome back, {firstName}! Here's your overview.
        </h1>
      </div>


      {/* Filters above KPI tiles per Adam's PDF — same date / mediator /
          search filter applies to tiles AND the charts below. */}
      <div style={{ padding: '12px 24px 0 24px' }}>
        <FilterBar
          searchValue={chartsSearch}
          onSearchChange={setChartsSearch}
          searchPlaceholder="Search by case ID, party name or mediator.."
          dateValue={chartsDateFilter}
          onDateChange={setChartsDateFilter}
          dateOptions={DEFAULT_DATE_PRESETS}
          dateDefaultValue="all"
          customStart={chartsCustomStart}
          customEnd={chartsCustomEnd}
          onCustomDateChange={(s, e) => { setChartsCustomStart(s); setChartsCustomEnd(e); }}
          mediatorValue={chartsMediatorFilter}
          onMediatorChange={setChartsMediatorFilter}
          mediators={mediatorsList}
        />
      </div>

      {/* A failed /dashboard call used to render an entirely blank page with
          no explanation. Say so, and offer the retry, rather than showing
          empty tiles that look like "you have no data". */}
      {loadFailed && !loading && (
        <div style={{ padding: '0 24px 12px 24px' }}>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-[#f0c2c2] bg-[#fdf3f3] px-4 py-3">
            <span className="text-sm text-[#8a2b2b]" style={{ fontFamily: 'Inter' }}>
              Some dashboard data could not be loaded. The figures shown may be incomplete.
            </span>
            <button
              type="button"
              onClick={() => setRefreshTick((t) => t + 1)}
              className="shrink-0 rounded-md bg-[#115e59] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#0f766e]"
              style={{ fontFamily: 'Inter' }}
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* KPI Metrics — order per Adam's PDF: Cases / Inquiries / Cases
          Booked / Admin Fee. Scale parity: each tile shows a period pill
          next to the label reflecting the active filter. */}
      {(() => {
        // Build the active-period pill label: preset label or custom date range.
        const presetLabel = (DEFAULT_DATE_PRESETS.find((p) => p.value === chartsDateFilter) || {}).label;
        const periodLabel =
          chartsDateFilter === 'custom' && (chartsCustomStart || chartsCustomEnd)
            ? `${chartsCustomStart || '…'} – ${chartsCustomEnd || '…'}`
            : (presetLabel || 'All time');
        return (
          <div className="flex gap-3" style={{ padding: '12px 24px' }}>
            <KPICardDesign
              title="Cases"
              value={stats?.totalCases ?? (loading ? '—' : '0')}
              change={stats?.change?.totalCases}
              periodLabel={periodLabel}
            />
            <KPICardDesign
              title="Inquiries"
              value={stats?.totalInquiries ?? (loading ? '—' : '0')}
              change={stats?.change?.totalInquiries}
              periodLabel={periodLabel}
            />
            <KPICardDesign
              title="Cases Booked"
              value={stats?.casesBooked ?? (loading ? '—' : '0')}
              change={stats?.change?.casesBooked}
              periodLabel={periodLabel}
            />
            <KPICardDesign
              title="Case Manager Fee"
              value={stats?.cmFee != null ? `$${Math.round(Number(stats.cmFee) || 0).toLocaleString('en-US')}` : (loading ? '—' : '$0')}
              change={stats?.change?.cmFee}
              periodLabel={periodLabel}
            />
          </div>
        );
      })()}

      {/* Charts Row 1 — Cases Over Time | Bookings Over Time (Adam's chart order: 5,6) */}
      <div className="flex items-center gap-5 border-b border-[#e5e5e5]" style={{ height: 276, padding: '0 24px' }}>
        {/* Cases Over Time — LINE chart */}
        <div className="flex-1 flex flex-col gap-5 bg-white" style={{ height: 230 }}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>
              Cases Over Time
            </span>
            {(() => {
              const cot = slicedCasesOverTime;
              const tm = cot?.thisMonth;
              const counts = cot?.counts || [];
              const labels = cot?.labels || [];
              const last = counts.length ? counts[counts.length - 1] : 0;
              const prev = counts.length > 1 ? counts[counts.length - 2] : 0;
              const diff = last - prev;
              const prevLabel = labels.length > 1 ? labels[labels.length - 2] : '';
              const monthFull = tm?.label ? (SHORT_MONTH_TO_FULL[tm.label] || tm.label) : '';
              const title = tm ? `${monthFull} ${tm.year}` : 'Cases Over Time';
              const activeMediators = stats?.activeMediators ?? 0;
              return (
                <ChartTooltip
                  title={title}
                  body={`Total Cases: ${last}\n${activeMediators} mediators active`}
                  badge={prevLabel ? { value: `${diff > 0 ? '+' : ''}${diff}`, label: prevLabel } : null}
                  badgeType={diff < 0 ? 'negative' : 'positive'}
                >
                  <HelpIcon />
                </ChartTooltip>
              );
            })()}
            <div className="ml-auto">
              <ChartRangeDropdown value={casesOverTimeRange} onChange={setCasesOverTimeRange} />
            </div>
          </div>
          <CasesOverTimeChart casesOverTime={slicedCasesOverTime} navigate={navigate} height={192} />
        </div>

        {/* Vertical Divider */}
        <div className="h-full w-px bg-[#e5e5e5]" />

        {/* Bookings Over Time — line chart of cases with mediation_date set */}
        <div className="flex-1 flex flex-col gap-5 bg-white" style={{ height: 230 }}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>
              Bookings Over Time
            </span>
            {(() => {
              const bot = slicedBookingsOverTime;
              const counts = bot?.counts || [];
              const labels = bot?.labels || [];
              const last = counts.length ? counts[counts.length - 1] : 0;
              const prev = counts.length > 1 ? counts[counts.length - 2] : 0;
              const diff = last - prev;
              const prevLabel = labels.length > 1 ? labels[labels.length - 2] : '';
              const tm = bot?.thisMonth;
              const monthFull = tm?.label ? (SHORT_MONTH_TO_FULL[tm.label] || tm.label) : '';
              const title = tm ? `${monthFull} ${tm.year}` : 'Bookings Over Time';
              return (
                <ChartTooltip
                  title={title}
                  body={`Total Bookings: ${last}\nMediations scheduled this month`}
                  badge={prevLabel ? { value: `${diff > 0 ? '+' : ''}${diff}`, label: prevLabel } : null}
                  badgeType={diff < 0 ? 'negative' : 'positive'}
                >
                  <HelpIcon />
                </ChartTooltip>
              );
            })()}
            <div className="ml-auto">
              <ChartRangeDropdown value={bookingsOverTimeRange} onChange={setBookingsOverTimeRange} />
            </div>
          </div>
          <BookingsOverTimeChart bookingsOverTime={slicedBookingsOverTime} navigate={navigate} height={192} />
        </div>
      </div>

      {/* Charts Row 2 — Inquiries Over Time | Top Mediators (per Adam: chart 7 + Top Mediators) */}
      <div className="flex items-center gap-5" style={{ height: 276, padding: '0 24px' }}>
        {/* Inquiries Over Time — stacked Converted / Not Converted bars
            with 2 y-axes (count + 0–100%). Legend dots in the header
            communicate the bar color split since there's no line. */}
        <div className="flex-1 flex flex-col gap-5 bg-white" style={{ height: 230 }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>
                Inquiries Over Time
              </span>
              {(() => {
                const ic = slicedInquiryConversion;
                const tm = ic?.thisMonth;
                const totals = ic?.totals || [];
                const converted = ic?.converted || [];
                const labels = ic?.labels || [];
                const lastTotal = totals.length ? totals[totals.length - 1] : 0;
                const lastConv = converted.length ? converted[converted.length - 1] : 0;
                const prevConv = converted.length > 1 ? converted[converted.length - 2] : 0;
                const diff = lastConv - prevConv;
                const prevLabel = labels.length > 1 ? labels[labels.length - 2] : '';
                const monthFull = tm?.label ? (SHORT_MONTH_TO_FULL[tm.label] || tm.label) : '';
                const title = tm?.label ? `${monthFull}` : 'Inquiries Over Time';
                const overallTotal = ic?.overall?.total ?? 0;
                return (
                  <ChartTooltip
                    title={title}
                    body={`Total Inquiries: ${overallTotal}\n${lastTotal} new this month`}
                    badge={prevLabel ? { value: `${diff > 0 ? '+' : ''}${diff}%`, label: prevLabel } : null}
                    badgeType={diff < 0 ? 'negative' : 'positive'}
                  >
                    <HelpIcon />
                  </ChartTooltip>
                );
              })()}
            </div>
            <ChartRangeDropdown value={inquiryConversionRange} onChange={setInquiryConversionRange} />
          </div>
          <InquiryConversionChart inquiryConversion={slicedInquiryConversion} navigate={navigate} height={192} />
        </div>

        {/* Top Mediators by Case Volume — gradient bar chart */}
        <div className="flex-1 flex flex-col gap-5 bg-white" style={{ height: 230 }}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>
              Top Mediators by Case Volume
            </span>
            {(() => {
              const tm = charts?.topMediators;
              const names = tm?.names || [];
              const counts = tm?.counts || [];
              const topName = names[0];
              const topCount = counts[0] ?? 0;
              const second = counts[1] ?? 0;
              const diff = topCount - second;
              return (
                <ChartTooltip
                  title={topName ? `Top: ${topName}` : 'Top Mediators'}
                  body={topName ? `${topCount} cases handled\nLeading the team this period` : 'No mediator activity yet'}
                  badge={topName && names[1] ? { value: `+${diff}`, label: names[1] } : null}
                  badgeType="positive"
                >
                  <HelpIcon />
                </ChartTooltip>
              );
            })()}
          </div>
          <TopMediatorsChart topMediators={charts?.topMediators} navigate={navigate} height={192} />
        </div>
      </div>

      {/* ── Upcoming Cases Table ── */}
      <div className="bg-white rounded-2xl relative" style={{ padding: '12px 24px 24px 24px' }}>
        {loading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[#5b5a64] font-['Inter']">Loading...</span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h2 className="text-lg font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
            Upcoming Cases
          </h2>
        </div>
        <FilterBar
          searchValue={tableSearch}
          onSearchChange={setTableSearch}
          searchPlaceholder="Search by case ID, party name or mediator.."
          mediatorValue={tableMediatorFilter}
          onMediatorChange={setTableMediatorFilter}
          // Use the canonical mediatorsList (same as the page-level
          // dropdown above) — previously this used tableMediatorOptions
          // derived from the displayed upcoming-cases rows, which
          // surfaced only the subset of mediators who currently had
          // cases in the visible window. That divergence was the "glitch"
          // — the page dropdown listed all mediators but the table
          // dropdown listed a smaller subset.
          mediators={mediatorsList}
        />

        <div className="rounded-xl bg-white overflow-hidden">
          <div className="flex items-center bg-[#f9fafb] border border-[#f0f0f0] rounded-t-xl" style={{ height: 45 }}>
            <div className="flex items-center h-full px-4 relative" style={{ width: colWidths.mediator }}>
              <SortableHeader
                sortKey="mediator"
                sort={tableSort}
                onSort={requestTableSort}
                className="text-xs font-medium text-[#5b5a64]"
                style={{ fontFamily: 'Inter' }}
              >Mediator</SortableHeader>
              <ResizeHandle onResize={setColWidth('mediator')} getCurrentWidth={() => colWidths.mediator} />
            </div>
            <div className="flex items-center h-full px-3 relative min-w-0" style={{ width: colWidths.caseName }}>
              <SortableHeader
                sortKey="caseName"
                sort={tableSort}
                onSort={requestTableSort}
                className="text-xs font-medium text-[#5b5a64]"
                style={{ fontFamily: 'Inter' }}
              >Case Name</SortableHeader>
              <ResizeHandle onResize={setColWidth('caseName')} getCurrentWidth={() => colWidths.caseName} />
            </div>
            <div className="flex items-center h-full px-3 relative" style={{ width: colWidths.date }}>
              <SortableHeader
                sortKey="date"
                sort={tableSort}
                onSort={requestTableSort}
                className="text-xs font-medium text-[#5b5a64]"
                style={{ fontFamily: 'Inter' }}
              >Mediation Date</SortableHeader>
              <ResizeHandle onResize={setColWidth('date')} getCurrentWidth={() => colWidths.date} />
            </div>
            <div className="flex items-center h-full px-3 relative" style={{ width: colWidths.plaintiff }}>
              <span className="text-xs font-semibold text-[#5b5a64] tracking-[0.3px]" style={{ fontFamily: 'Inter' }}>Plaintiff Status</span>
              <ResizeHandle onResize={setColWidth('plaintiff')} getCurrentWidth={() => colWidths.plaintiff} />
            </div>
            <div className="flex-1 flex items-center h-full px-3 min-w-0">
              <span className="text-xs font-semibold text-[#5b5a64] tracking-[0.3px]" style={{ fontFamily: 'Inter' }}>Defendant Status</span>
            </div>
          </div>

          {!loading && filteredDashboardCases.length === 0 && (
            <div className="flex items-center justify-center" style={{ height: 120 }}>
              <span className="text-sm text-[#5b5a64] font-['Inter']">
                {dashboardCases.length === 0 ? 'No upcoming cases' : 'No matches for your search'}
              </span>
            </div>
          )}
          {pagedUpcomingCases.map((row) => (
            <div
              key={row.id}
              className="flex items-center border-b border-[#f0f0f0] cursor-pointer hover:bg-gray-50 transition-colors"
              style={{ height: 48 }}
              onClick={() => navigate(`/cases/${row.id}`)}
            >
              <div className="flex items-center h-full px-4" style={{ width: colWidths.mediator }}>
                <span className="text-[13px] font-normal text-[#0e1e20] truncate" style={{ fontFamily: 'Inter' }}>{row.mediator}</span>
              </div>
              <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.caseName }}>
                <span className="text-[13px] font-normal text-[#0e1e20] truncate" style={{ fontFamily: 'Inter' }}>{row.caseName}</span>
              </div>
              <div className="flex items-center h-full px-3" style={{ width: colWidths.date }}>
                <span className="text-[13px] font-normal text-[#0e1e20] truncate" style={{ fontFamily: 'Inter' }}>{row.date}</span>
              </div>
              <div className="flex items-center gap-3 h-full px-3" style={{ width: colWidths.plaintiff }}>
                <StatusCell status={row.plaintiff} briefLabel={row.briefText} />
              </div>
              <div className="flex-1 flex items-center gap-3 h-full px-3 min-w-0">
                <StatusCell status={row.defendant} briefLabel={row.briefText} />
              </div>
            </div>
          ))}
        </div>

        <Pagination
          currentPage={upcomingPage}
          totalPages={upcomingTotalPages}
          totalEntries={upcomingTotal}
          pageSize={upcomingPageSize}
          onPageChange={setUpcomingPage}
          onPageSizeChange={setUpcomingPageSize}
        />
      </div>
    </div>
  );
}
